import { useEffect, useMemo, useRef, useState } from "react";
import { Gauge, LayoutGrid, Search, Table2 } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  FilterMenu,
  Input,
  Modal,
  SectionHeader,
  SegmentedControl,
  TabPanel,
  Tabs,
  useFilterMenuController,
  useFilterUrlState,
  usePeopleSource,
} from "@playbookai/ui";
import {
  KpiOwnerDirectoryProvider,
  type KpiOwnerDirectory,
} from "../owner/owner-directory.js";
import type { KpiListFilters } from "../api/types.js";
import {
  buildAllKpisFilterSchema,
  KPI_OWNER_FILTER_ID,
} from "../filters/kpi-filter-schema.js";
import {
  ownerIdFallbackLabel,
  useOwnerFilterOptions,
} from "../filters/useOwnerFilterOptions.js";
import { useDebouncedValue } from "../hooks/useDebouncedValue.js";
import { useOwnerNames } from "../hooks/useOwnerNames.js";
import { KpiListView, type KpiCollectionLayout } from "./KpiListView.js";
import { KpiDetailPage } from "./KpiDetailPage.js";
import { CreateKpiPage } from "./CreateKpiPage.js";
import { EditKpiPage } from "./EditKpiPage.js";
import { KpiLibraryView } from "./KpiLibraryView.js";
import { ArchivedKpiView } from "./ArchivedKpiView.js";

/** The three collections. The host may drive it from the route (PB-5134, d3). */
export type KpiManagerSection = "all" | "library" | "archived";

// Stable identity for "no owners selected", so the `filters` memo (and the
// list query key) is not invalidated by a fresh `[]` every render.
const NO_OWNER_IDS: readonly string[] = Object.freeze([]);

/** The three collections as the canvas' sub-tabs (PB-5134, d35). */
const SECTION_ITEMS = [
  { value: "all", label: "All KPIs" },
  { value: "library", label: "KPI Library" },
  { value: "archived", label: "Archived" },
] as const;

/** How the Create KPI page was left. `created` belongs on All KPIs with the new
 * KPI open; `cancelled` (its Cancel button) belongs back on whichever KPI
 * screen preceded it; `all-kpis` (its "All KPIs" link) belongs on All KPIs. */
export type KpiCreateOutcome = "created" | "cancelled" | "all-kpis";

/** How the Edit page was left. `saved` belongs on the KPI's page. `cancelled`
 * (a clean form) belongs back where Edit was opened from. `left` is every other
 * exit — a Cancel with unsaved changes, or the section rail — which must land
 * on the KPI's page so the discard prompt can show. */
export type KpiEditCloseOutcome = "saved" | "cancelled" | "left";

export interface KpiManagerProps {
  /**
   * The host's owner directory (its directory groups). The owner fields and
   * names read the host's `PeopleSource` (a `PeopleSourceProvider` around
   * this component).
   */
  readonly ownerDirectory: KpiOwnerDirectory;
  /**
   * Tenant-admin — show the create + GOVERNANCE affordances (assign owner, set
   * target, activate, archive). The server is still the authoritative gate; this
   * only hides UI. Defaults to false.
   *
   * It does NOT gate `Add value`, which only the KPI OWNER can use. A
   * Tenant-Admin has no override (ADR-0052).
   */
  readonly canManage?: boolean;
  /** Route-derived: the dedicated Create KPI page is the active view. This
   * package owns no router (ADR-0009) — the shell derives it from the URL. */
  readonly createMode?: boolean;
  /** Route-derived `?definitionId`: the KPI Type the Library sent along. */
  readonly createDefinitionId?: string | null;
  /** Navigate to the Create KPI page, optionally preselecting a KPI Type. */
  readonly onOpenCreate?: (definitionId: string | null) => void;
  /** Leave the Create KPI page. */
  readonly onCloseCreate?: (outcome: KpiCreateOutcome) => void;
  /**
   * Route-derived `/settings/kpi/:kpiId` (PB-2941): the KPI whose detail page is
   * the active view, or null for the collection. This package owns no router
   * (ADR-0009), so the shell derives it from the URL and this component never
   * holds the selection in state — which is exactly what makes the page
   * deep-linkable, refresh-safe, and restorable by browser Back.
   */
  readonly detailKpiId?: string | null;
  /** Navigate to a KPI's detail page. */
  readonly onOpenKpi?: (kpiId: string) => void;
  /** Leave the detail page for the collection it came from. */
  readonly onCloseKpi?: () => void;
  /**
   * Route-derived `/settings/kpi/:kpiId/edit` (PB-2941): the KPI's edit page is
   * the active view. A THIRD route-derived flag rather than a modal flag, for
   * the same reason `createMode` is one — the edit form is a page, so it is
   * addressable, refresh-safe, and left by browser Back.
   */
  readonly editMode?: boolean;
  /** Navigate to a KPI's edit page — from its detail page (`detailKpiId` set;
   * the host REPLACES that entry) or from the collection's row menu (none set;
   * the host PUSHES and remembers the collection, so Back and a clean Cancel
   * return to the filtered list instead of skipping it). */
  readonly onOpenEdit?: (kpiId: string) => void;
  /** The host router's push, so applied filters reach the router's location
   * rather than going round it with a raw `pushState`. */
  readonly onNavigateUrl?: (url: string) => void;
  /** Open the Target-to-Improve page for a KPI's target (PB-5317). The feature
   * owns no router (ADR-0009); a host that has not wired the route simply
   * offers no link. */
  readonly onOpenTti?: (ttiId: string) => void;
  /** Leave the edit page for the KPI it belongs to. */
  readonly onCloseEdit?: (outcome: KpiEditCloseOutcome) => void;
  /**
   * Route-derived section (PB-5134, d3): when the host owns the section — the
   * Settings rail lists All KPIs, KPI Library and Archived as child items —
   * it passes the current one here and receives changes through
   * `onSectionChange` instead of this component keeping the choice in state.
   * Omit both to keep the component's own section rail and state.
   */
  readonly section?: KpiManagerSection;
  readonly onSectionChange?: (section: KpiManagerSection) => void;
  /** Hide the component's own section rail because the host renders one. */
  readonly hideSectionNav?: boolean;
}

/**
 * The KPI Manager (PB-94, canonical Chunk 3). Owns the browse search, card/table
 * presentation, and the two ROUTED children of All KPIs — the Create KPI page
 * and a KPI's detail page (PB-2941, which replaced the detail drawer). Mounted
 * by the shell, which supplies the people source and — since this package owns
 * no router — every route-derived value and navigation callback.
 *
 * The shell decides who reaches which URL: browse and create are Tenant-Admin, a
 * KPI's own page is any tenant member's (a KPI Owner is routinely not an admin).
 * This component is handed the answer as `canManage` and never re-derives it.
 *
 * Note what stays in component state and what does not. The section rail, the
 * search text and the card/table layout are LOCAL, and survive a trip to a child
 * page because this component stays mounted across all three URLs (the shell
 * mounts one host for `/settings/kpi`, `/create` and `/:kpiId`). The applied
 * filters live in the URL, and the loaded pages live in the TanStack cache. The
 * SELECTED KPI is none of these — it is the URL, which is what makes a KPI
 * linkable, refresh-safe, and restorable by browser Back with the collection
 * exactly as it was left.
 */
export function KpiManager({
  ownerDirectory,
  canManage = false,
  createMode = false,
  createDefinitionId = null,
  onOpenCreate,
  onCloseCreate,
  detailKpiId = null,
  onOpenKpi,
  onCloseKpi,
  editMode = false,
  onOpenEdit,
  onNavigateUrl,
  onOpenTti,
  onCloseEdit,
  section: controlledSection,
  onSectionChange,
  hideSectionNav = false,
}: KpiManagerProps): JSX.Element {
  const [ownSection, setOwnSection] = useState<KpiManagerSection>("all");
  const section = controlledSection ?? ownSection;
  const setSection = (next: KpiManagerSection): void => {
    if (controlledSection === undefined) setOwnSection(next);
    onSectionChange?.(next);
  };
  const [layout, setLayout] = useState<KpiCollectionLayout>("cards");
  const [q, setQ] = useState("");
  const [createDirty, setCreateDirty] = useState(false);
  const [editDirty, setEditDirty] = useState(false);

  // The Type the create route carried, kept so "Keep editing" returns to the
  // same URL after the route has already moved on.
  const createDefinitionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (createMode) createDefinitionIdRef.current = createDefinitionId;
  }, [createMode, createDefinitionId]);

  // Which KPI the dirty EDIT draft belongs to. `editDirty` alone cannot say,
  // and without the answer the flag outlived its KPI context: leaving the KPI
  // entirely kept the discard prompt up with a "Keep editing" that had nowhere
  // to go (`detailKpiId` was null), and navigating to ANOTHER KPI's page
  // force-opened THAT KPI's edit form over an unrelated draft. Reset during
  // render, not in an effect — an effect lets one render through with the
  // stale pairing, and that render mounts the wrong KPI's edit page (the same
  // reasoning as the TTI page's draft reset).
  const [editScopeKpiId, setEditScopeKpiId] = useState<string | null>(null);
  if (editMode && detailKpiId && editScopeKpiId !== detailKpiId) {
    setEditScopeKpiId(detailKpiId);
  }
  if (editDirty && detailKpiId !== editScopeKpiId) {
    setEditDirty(false);
  }

  // An edited draft outlives the route change, so EVERY way out of the create
  // page — Cancel, the section rail, the breadcrumb, browser Back — lands on
  // the same prompt instead of silently dropping what was typed. (Refresh and
  // close are the browser's to guard; CreateKpiPage arms `beforeunload`.)
  const showCreate = canManage && (createMode || createDirty);
  const confirmDiscardCreate = createDirty && !createMode;
  // The edit page is a child of a KPI's page and gets the same treatment as
  // Create, for the same reason: an edited form outlives the route change, so
  // every way out prompts rather than silently dropping what was typed. It is
  // admin-gated because the endpoint behind it is (`PATCH /v1/kpis/:id`).
  const showEdit = canManage && Boolean(detailKpiId) && (editMode || editDirty);
  // `Boolean(detailKpiId)` explicitly, though the scope reset above already
  // clears a context-less `editDirty`: the prompt must never open somewhere
  // "Keep editing" cannot return from.
  const confirmDiscardEdit = Boolean(detailKpiId) && editDirty && !editMode;
  // A KPI's detail page is a child of All KPIs, like Create — and, like Create,
  // it yields to an unsaved draft so leaving the form always prompts first.
  const showDetail = Boolean(detailKpiId) && !showCreate && !showEdit;

  const handleSectionChange = (value: KpiManagerSection) => {
    setSection(value);
    if (editMode) onCloseEdit?.("left");
    if (detailKpiId) onCloseKpi?.();
    if (createMode) onCloseCreate?.("cancelled");
  };

  /** Return to the edit URL the unsaved draft belongs to. Named because both the
   * prompt's dismissal and its "Keep editing" button do the same thing. */
  const keepEditing = () => {
    if (detailKpiId) onOpenEdit?.(detailKpiId);
  };

  const handleCreated = (kpiId: string) => {
    // Cleared BEFORE the route moves, so a successful create never prompts.
    setCreateDirty(false);
    // Local state only: a controlled host REPLACES the create entry itself in
    // `onCloseCreate`, and a push here would leave the submitted form one Back
    // away.
    if (controlledSection === undefined) setOwnSection("all");
    onCloseCreate?.("created");
    // Land on the new KPI's own page. `onCloseCreate` REPLACES the create entry
    // with All KPIs, so Back from here goes to the collection rather than back
    // into the form that was just submitted.
    onOpenKpi?.(kpiId);
  };

  // All three are descendants of All KPIs, so the rail keeps pointing there.
  const activeSection = showCreate || showDetail || showEdit ? "all" : section;

  return (
    <KpiOwnerDirectoryProvider value={ownerDirectory}>
      <div className="@container/kpi min-w-0">
        {/* No background, width or scrolling of its own: the host's lane paints
            and pads the page like every other Settings page (d35). The named
            container stays for the child pages' container queries. */}
        <div className="@container/content min-w-0">
          <div className="space-y-5">
            {showCreate || showEdit || showDetail ? null : (
              <>
                <SectionHeader
                  variant="settings"
                  leading={<Gauge className="w-4 h-4 text-pb-cyan-text" />}
                  title="KPI Manager"
                  description="Govern KPI definitions, owners and targets across the workspace."
                  badge={canManage ? <Badge>Admin</Badge> : undefined}
                  actionsSlot={
                    canManage ? (
                      <Button
                        variant="primary"
                        onClick={() => onOpenCreate?.(null)}
                      >
                        Create KPI
                      </Button>
                    ) : undefined
                  }
                />
                {hideSectionNav ? null : (
                  <Tabs
                    aria-label="KPI Manager sections"
                    items={[...SECTION_ITEMS]}
                    value={activeSection}
                    onValueChange={(value) =>
                      handleSectionChange(value as KpiManagerSection)
                    }
                  >
                    <TabPanel value={activeSection} />
                  </Tabs>
                )}
              </>
            )}
            {/* Keyed on what is shown, so every swap — a section, a KPI's
                page, its Create or Edit form — arrives with the content-enter
                transition instead of snapping in place. */}
            <div
              key={
                showCreate
                  ? "create"
                  : showEdit
                    ? `edit:${detailKpiId}`
                    : showDetail
                      ? `detail:${detailKpiId}`
                      : section
              }
              className="pb-motion-content-enter min-w-0"
            >
              {showCreate ? (
                <CreateKpiPage
                  initialDefinitionId={createDefinitionId}
                  onCancel={() => onCloseCreate?.("cancelled")}
                  onBackToAll={() => onCloseCreate?.("all-kpis")}
                  onCreated={handleCreated}
                  onDirtyChange={setCreateDirty}
                />
              ) : showEdit && detailKpiId ? (
                <EditKpiPage
                  kpiId={detailKpiId}
                  onCancel={() =>
                    // A dirty form stays on the KPI's page so the discard
                    // prompt can show; only a clean Cancel goes back.
                    onCloseEdit?.(editDirty ? "left" : "cancelled")
                  }
                  // Leaving the edit form goes to the KPI; this says All KPIs.
                  onBackToAll={() => onCloseKpi?.()}
                  onSaved={() => {
                    // Cleared BEFORE the route moves, so a successful save
                    // never prompts on the way out.
                    setEditDirty(false);
                    onCloseEdit?.("saved");
                  }}
                  onDirtyChange={setEditDirty}
                />
              ) : showDetail && detailKpiId ? (
                <KpiDetailPage
                  kpiId={detailKpiId}
                  onBack={() => onCloseKpi?.()}
                  canManage={canManage}
                  onEdit={
                    onOpenEdit ? () => onOpenEdit(detailKpiId) : undefined
                  }
                  {...(onOpenTti ? { onOpenTti } : {})}
                />
              ) : section === "all" ? (
                <AllKpisSection
                  q={q}
                  onQChange={setQ}
                  layout={layout}
                  onLayoutChange={setLayout}
                  onSelectKpi={(kpiId) => onOpenKpi?.(kpiId)}
                  canManage={canManage}
                  {...(onOpenEdit ? { onEdit: onOpenEdit } : {})}
                  {...(onNavigateUrl ? { onNavigateUrl } : {})}
                />
              ) : section === "library" ? (
                <KpiLibraryView
                  canManage={canManage}
                  onUseDefinition={(definitionId) =>
                    onOpenCreate?.(definitionId)
                  }
                />
              ) : (
                <ArchivedKpiView
                  onSelectKpi={(kpiId) => onOpenKpi?.(kpiId)}
                  canManage={canManage}
                />
              )}
            </div>
          </div>
        </div>
        <Modal
          open={confirmDiscardCreate}
          onClose={() => onOpenCreate?.(createDefinitionIdRef.current)}
          size="sm"
          title="Discard this KPI?"
          description="The KPI has not been created. Leaving now discards what you entered."
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => onOpenCreate?.(createDefinitionIdRef.current)}
              >
                Keep editing
              </Button>
              <Button
                variant="destructive"
                onClick={() => setCreateDirty(false)}
              >
                Discard
              </Button>
            </>
          }
        />
        {/* The same prompt for the edit form. Worded for a CHANGE rather than a
            creation — "discard this KPI" would read, wrongly, as deleting it. */}
        <Modal
          open={confirmDiscardEdit}
          onClose={keepEditing}
          size="sm"
          title="Discard these changes?"
          description="The KPI has not been updated. Leaving now discards what you changed."
          footer={
            <>
              <Button variant="ghost" onClick={keepEditing}>
                Keep editing
              </Button>
              <Button variant="destructive" onClick={() => setEditDirty(false)}>
                Discard
              </Button>
            </>
          }
        />
      </div>
    </KpiOwnerDirectoryProvider>
  );
}

/**
 * The All KPIs collection (PB-2941): search + layout toggle + the schema-driven
 * Owner filter. The filter's option source and chip labels reach the host's
 * `PeopleSource` (the shell's provider) via context.
 * The Owner selection lives in the URL (applied values only — refresh and
 * Back/Forward restore it), request-maps onto `GET /v1/kpis` via
 * `ownerUserIds`, and resets the cursor by construction: new filters mean a
 * new infinite-query key starting from the first page.
 */
function AllKpisSection({
  q,
  onQChange,
  layout,
  onLayoutChange,
  onSelectKpi,
  canManage,
  onEdit,
  onNavigateUrl,
}: {
  readonly q: string;
  readonly onQChange: (q: string) => void;
  readonly layout: KpiCollectionLayout;
  readonly onLayoutChange: (layout: KpiCollectionLayout) => void;
  readonly onSelectKpi: (kpiId: string) => void;
  readonly canManage: boolean;
  readonly onEdit?: (kpiId: string) => void;
  readonly onNavigateUrl?: (url: string) => void;
}): JSX.Element {
  const peopleSource = usePeopleSource();
  const ownerOptions = useOwnerFilterOptions(peopleSource);
  const schema = useMemo(
    () => buildAllKpisFilterSchema(ownerOptions),
    [ownerOptions],
  );
  const { applied, setApplied } = useFilterUrlState(schema, {
    navigate: onNavigateUrl,
  });
  const filterController = useFilterMenuController({
    schema,
    applied,
    onAppliedChange: setApplied,
  });

  // Chip/pinned-value labels reuse the SAME cached id→name resolution the
  // list's OwnerChips use; an unresolved id renders its truthful fallback.
  // (`applied` is controller-memoized, so this reference — and therefore the
  // `filters` memo below — only changes when the selection actually does.)
  const appliedOwnerIds =
    filterController.applied[KPI_OWNER_FILTER_ID] ?? NO_OWNER_IDS;
  const ownerNames = useOwnerNames(appliedOwnerIds).data;
  const ownerValueLabel = (fieldId: string, value: string) =>
    fieldId === KPI_OWNER_FILTER_ID
      ? (ownerNames?.[value]?.name ?? ownerIdFallbackLabel(value))
      : undefined;

  // Debounce the search text so the server-filtered list query runs on the
  // settled value, not on every keystroke. The input stays immediate.
  const debouncedQ = useDebouncedValue(q, 250);
  const filters = useMemo<KpiListFilters>(() => {
    const next: { -readonly [K in keyof KpiListFilters]?: KpiListFilters[K] } =
      {};
    const trimmed = debouncedQ.trim();
    if (trimmed) next.q = trimmed;
    if (appliedOwnerIds.length > 0) next.ownerUserIds = appliedOwnerIds;
    // Members browse only KPIs assigned to them. Admins retain the tenant-wide
    // governance view, including unassigned KPIs that still need an owner.
    if (!canManage) next.mine = true;
    return next;
  }, [debouncedQ, appliedOwnerIds, canManage]);

  return (
    <section aria-labelledby="all-kpis-heading" className="space-y-4">
      <h2 id="all-kpis-heading" className="sr-only">
        All KPIs
      </h2>
      {canManage ? null : (
        <Alert tone="info">
          Contact a Transformation Lead to create or manage KPIs. As an owner,
          you can record values and set targets for your KPIs.
        </Alert>
      )}
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <Input
            type="search"
            aria-label="Search KPIs"
            placeholder="Search KPIs…"
            value={q}
            onChange={(event) => onQChange(event.target.value)}
            prefix={<Search className="h-3.5 w-3.5" />}
          />
        </div>
        <div className="flex items-center gap-2.5 self-start sm:shrink-0 sm:self-auto">
          {/* OK-09: 32px-wide hand-built icon segments → SegmentedControl
              iconOnly, 26px squares. */}
          <SegmentedControl
            size="sm"
            value={layout}
            onChange={onLayoutChange}
            aria-label="KPI layout"
            className="shrink-0"
            options={[
              {
                value: "cards",
                label: "Cards",
                icon: <LayoutGrid className="h-4 w-4" aria-hidden />,
                iconOnly: true,
              },
              {
                value: "table",
                label: "Table",
                icon: <Table2 className="h-4 w-4" aria-hidden />,
                iconOnly: true,
              },
            ]}
          />
          {/* The trigger reads the applied selection, while the menu retains
              remote owner search and URL-backed state (PB-2941). */}
          <FilterMenu
            controller={filterController}
            valueLabel={ownerValueLabel}
            showActiveCount={false}
            triggerLabel={
              appliedOwnerIds.length === 0
                ? "All owners"
                : appliedOwnerIds.length === 1
                  ? (ownerValueLabel(
                      KPI_OWNER_FILTER_ID,
                      appliedOwnerIds[0]!,
                    ) ?? "1 owner")
                  : `${appliedOwnerIds.length} owners`
            }
          />
        </div>
      </div>
      <KpiListView
        filters={filters}
        onSelect={onSelectKpi}
        layout={layout}
        canManage={canManage}
        {...(onEdit ? { onEdit } : {})}
      />
    </section>
  );
}
